/**
 * Challenge leaderboard — podium for top 3 + paginated ranked list.
 * Current user row highlighted with glow.
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Medal } from 'lucide-react'

import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { Button } from '@turnifly/shared/ui'
import { useLeaderboardPage } from '@turnifly/shared/hooks'
import type { LeaderboardEntry } from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeLeaderboardTabProps {
  challengeId: string
  top3: LeaderboardEntry[]
  userId: string | undefined
}

// ── Constants ────────────────────────────────────────────────

const MEDALS = ['🥇', '🥈', '🥉']
const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']
const PODIUM_H = ['h-28', 'h-20', 'h-16']

// ── Leaderboard row ──────────────────────────────────────────

function LeaderboardRow({ entry, index, isCurrentUser }: {
  entry: LeaderboardEntry
  index: number
  isCurrentUser: boolean
}): React.ReactElement {
  const isTop3 = index < 3
  const rate = Math.round(entry.completion_rate)

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-colors ${
        isCurrentUser
          ? 'border border-[var(--color-primary)]/15 bg-[var(--color-primary)]/[0.08] shadow-[0_0_12px_rgba(0,97,255,0.06)]'
          : isTop3
            ? 'bg-white/[0.04]'
            : 'hover:bg-white/[0.02]'
      }`}
    >
      <span
        className="w-7 text-center text-[14px] font-bold tabular-nums"
        style={{ color: isTop3 ? RANK_COLORS[index] : 'var(--color-text-hint)' }}
      >
        {index + 1}
      </span>
      <AvatarWithBadge
        src={entry.avatar_url}
        fallback={entry.full_name}
        badge={getBadgeDisplay(false, entry.badge_type)}
        size="sm"
      />
      <span className="flex-1 truncate text-[13px] font-semibold text-white">
        {entry.full_name}
      </span>
      {isTop3 && <span className="text-base">{MEDALS[index]}</span>}
      <div className="flex shrink-0 items-center gap-1.5">
        <div className="h-1.5 w-12 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${rate}%`,
              background: isTop3
                ? `linear-gradient(90deg, ${RANK_COLORS[index]}60, ${RANK_COLORS[index]})`
                : 'var(--color-primary)',
            }}
          />
        </div>
        <span className="w-9 text-right text-[12px] font-bold tabular-nums text-white">
          {rate}%
        </span>
      </div>
    </motion.div>
  )
}

// ── Podium ───────────────────────────────────────────────────

function Podium({ top3 }: { top3: LeaderboardEntry[] }): React.ReactElement | null {
  if (top3.length < 3) return null

  const order = [1, 0, 2] // 2nd, 1st, 3rd

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="flex items-end justify-center gap-3 px-4 pb-3 pt-5"
    >
      {order.map((idx) => {
        const entry = top3[idx]
        if (!entry) return null
        const place = idx + 1

        return (
          <div key={entry.user_id} className="flex w-[90px] flex-col items-center">
            <div className="relative">
              <div
                className="absolute -inset-1 rounded-full opacity-30 blur-md"
                style={{ backgroundColor: RANK_COLORS[idx] }}
              />
              <AvatarWithBadge
                src={entry.avatar_url}
                fallback={entry.full_name}
                badge={getBadgeDisplay(false, entry.badge_type)}
                size={place === 1 ? 'lg' : 'md'}
              />
            </div>
            <span className="mt-1 text-xl">{MEDALS[idx]}</span>
            <p className="mt-0.5 w-full truncate text-center text-[11px] font-semibold text-white">
              {entry.full_name}
            </p>
            <p
              className="text-[10px] font-bold tabular-nums"
              style={{ color: RANK_COLORS[idx] }}
            >
              {Math.round(entry.completion_rate)}%
            </p>
            <div
              className={`mt-2 w-full rounded-t-xl ${PODIUM_H[idx]}`}
              style={{
                background: `linear-gradient(to top, ${RANK_COLORS[idx]}25, ${RANK_COLORS[idx]}08)`,
                borderTop: `2px solid ${RANK_COLORS[idx]}40`,
              }}
            />
          </div>
        )
      })}
    </motion.div>
  )
}

// ── Main component ───────────────────────────────────────────

export function ChallengeLeaderboardTab({
  challengeId,
  top3,
  userId,
}: ChallengeLeaderboardTabProps): React.ReactElement {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const { data: pageData, isFetching } = useLeaderboardPage(challengeId, page)

  const allEntries = useMemo(() => {
    if (page === 0 && pageData) return pageData
    return pageData ?? top3
  }, [pageData, top3, page])

  if (top3.length === 0) {
    return (
      <div className="py-16 text-center">
        <Medal
          size={40}
          className="mx-auto mb-3 text-[var(--color-text-hint)]/15"
        />
        <p className="text-[13px] text-[var(--color-text-hint)]">
          {t('challenges.profile.noMembers')}
        </p>
      </div>
    )
  }

  return (
    <div className="pb-6">
      {top3.length >= 3 && <Podium top3={top3} />}

      <div className="mt-2 space-y-1.5 px-4">
        {allEntries.map((entry, i) => (
          <LeaderboardRow
            key={entry.user_id}
            entry={entry}
            index={page * 20 + i}
            isCurrentUser={entry.user_id === userId}
          />
        ))}
      </div>

      {allEntries.length >= 20 && (
        <div className="mt-4 px-4">
          <Button
            variant="glass"
            size="sm"
            fullWidth
            isLoading={isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('challenges.profile.loadMore')}
          </Button>
        </div>
      )}
    </div>
  )
}
